import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { ALPHA, BADGE, FONT_SIZE, HOLIDAY_GROUPS, INK, MOTION, NEUTRAL } from "../../../theme/tokens";

const CELL_BOX_SX = { width: "100%", px: 1, py: 0.75, mx: -1 } as const;

type Props = {
	count: number;
	/** The row's largest cell — the bar is drawn against it, so an uneven row shows at a glance. */
	rowMax: number;
	/** The group's colour, for the bar. */
	color: string;
	ariaLabel: string;
	onClick: () => void;
	dataTestId: string;
};

/** A Group balance number over a thin bar; clicking it lists those people on the Team page. 0 is plain text. */
export const HolidayBalanceCell: FC<Props> = ({ count, rowMax, color, ariaLabel, onClick, dataTestId }) => {
	const share = rowMax === 0 ? 0 : count / rowMax;
	const figure = (
		<Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.5, width: "100%", minWidth: HOLIDAY_GROUPS.BALANCE_CELL_MIN_WIDTH_PX }}>
			<Box
				component="span"
				sx={{
					fontSize: FONT_SIZE.TITLE_SM,
					fontWeight: 700,
					fontVariantNumeric: "tabular-nums",
					color: count === 0 ? INK.DISABLED : INK.PRIMARY,
					textAlign: "start",
				}}
			>
				{count}
			</Box>
			<Box
				aria-hidden
				sx={{ height: HOLIDAY_GROUPS.BALANCE_BAR_HEIGHT_PX, borderRadius: `${BADGE.PILL_RADIUS_FULL}px`, backgroundColor: NEUTRAL[150], overflow: "hidden" }}
			>
				<Box
					sx={{
						height: "100%",
						width: "100%",
						backgroundColor: color,
						borderRadius: "inherit",
						transformOrigin: "left center",
						transform: `scaleX(${share})`,
						transition: `transform ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
						"[dir='rtl'] &": { transformOrigin: "right center" },
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					}}
				/>
			</Box>
		</Box>
	);
	// The same box as the button, so a 0 lines up with the numbers beside it.
	if (count === 0)
		return (
			<Box data-testid={dataTestId} sx={CELL_BOX_SX}>
				{figure}
			</Box>
		);
	return (
		<ButtonBase
			onClick={onClick}
			aria-label={ariaLabel}
			data-testid={dataTestId}
			sx={(theme) => ({
				...CELL_BOX_SX,
				borderRadius: 1,
				justifyContent: "flex-start",
				transition: `background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) } },
				"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
				"&:active": { transform: "scale(0.97)" },
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			})}
		>
			{figure}
		</ButtonBase>
	);
};
